import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  ExternalLink,
  Mail,
  Phone,
  Plus,
  Trash2,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import type { components } from "@/api/schema";
import {
  SUPPLIER_KIND_LABELS,
  useAddSourcingContact,
  useCreateSourcingOption,
  useDeleteSourcingOption,
  useReorderSourcingOptions,
  useSaveSourcingOption,
  useSuppliers,
  useUpdateSupplier,
  type SourcingOption,
  type Supplier,
} from "@/api/sourcing";
import {
  useRemoveManufacturerContact,
  useUpdateContact,
  useUpdateManufacturerContact,
  useManufacturerCardFees,
  useUpdateManufacturerPaymentMethod,
  type ManufacturerCaseTerms,
  type ManufacturerPaymentMethod,
} from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { InlineFieldInput } from "@/components/inline-field-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ActionPopover } from "@/components/action-popover";
import { LoadingButton } from "@/components/loading-button";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { SettingCell } from "@/components/setting-cell";
import { SupplierPicker } from "@/components/supplier-picker";
import { describePaymentTerms } from "@/features/manufacturers/payment-terms";
import { CardFeeField } from "@/features/manufacturers/card-fee-field";
import {
  PurchasingTermsGrid,
  type FeeRule,
} from "@/features/manufacturers/purchasing-terms-grid";
import { formatPhone } from "@/lib/phone";
import { LeadTimeCheckSetting } from "@/features/manufacturer-data/lead-time-check-setting";
import { cn } from "@/lib/utils";
import {
  FreightProgram,
  type FreightRuleData,
} from "@/features/manufacturers/freight-program";
import {
  BackupDiscounts,
  LiveDiscounts,
} from "@/features/manufacturers/source-discounts";
import { SourceSpecials } from "@/features/manufacturers/source-specials";
import { SourceCategoriesField } from "@/features/manufacturers/source-categories-field";

type ContactLink = components["schemas"]["ManufacturerContactLinkRead"];
type Discount = components["schemas"]["ManufacturerDiscountRead"];
type SpecialProgramSummary =
  components["schemas"]["ManufacturerSpecialProgramSummary"];
type AccountStatus = NonNullable<SourcingOption["account_status"]>;
type OrderMethod = SourcingOption["order_method"];

const ACCOUNT_STATUS_LABELS: Record<AccountStatus, string> = {
  open: "Account open",
  pending: "Account pending",
  opening_order_needed: "Needs opening order",
  not_opened: "No account",
  not_required: "No account needed",
};

const ORDER_METHOD_OPTIONS: Array<{ value: OrderMethod; label: string }> = [
  { value: "email", label: "Email PO" },
  { value: "online", label: "Online" },
  { value: "other", label: "Other" },
];

/** A manufacturer with no case terms sells broken cases at no fee. */
const DEFAULT_CASE_TERMS: ManufacturerCaseTerms = {
  full_cases_only: false,
  broken_case_fee: null,
  broken_case_fee_max: null,
  broken_case_discount: null,
};

export type SourceSheet = number | "new" | "contacts" | null;

function sourceName(option: SourcingOption): string {
  return option.supplier?.name ?? "Direct";
}

function isOrderRole(role: string): boolean {
  return /^po_(primary|backup|last_resort)(_cc)?$/.test(role);
}

function isCc(role: string): boolean {
  return role.endsWith("_cc");
}

function errorDetail(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

function orderMethodLabel(option: SourcingOption): string {
  return (
    ORDER_METHOD_OPTIONS.find((item) => item.value === option.order_method)
      ?.label ?? "Email PO"
  );
}

function leadTime(days: number | null | undefined): string | null {
  return days != null
    ? `${days} business ${days === 1 ? "day" : "days"}`
    : null;
}

function websiteHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export function SourcingPanel({
  manufacturerUuid,
  manufacturerName,
  options,
  contactLinks,
  poRecipientEmail,
  defaultFreightMarkup,
  discounts,
  freightRules,
  feeRules,
  caseTerms,
  paymentMethod,
  shipsToResidential,
  specialPrograms,
  openId: controlledOpenId,
  onOpenIdChange,
}: {
  manufacturerUuid: string;
  manufacturerName: string;
  options: SourcingOption[];
  discounts: Discount[];
  freightRules: FreightRuleData[];
  feeRules: FeeRule[];
  /** Full-case selling and the broken-case fee (USA-1496); unset is the defaults. */
  caseTerms?: ManufacturerCaseTerms | null;
  /** How we pay the manufacturer (USA-1470); unset is ACH. */
  paymentMethod?: ManufacturerPaymentMethod | null;
  /** Whether the manufacturer delivers to homes (USA-1683); unset is yes. */
  shipsToResidential?: boolean;
  specialPrograms: SpecialProgramSummary[];
  /** Which source's sheet is open, when the page controls it (the overview opens the preferred one). */
  openId?: SourceSheet;
  onOpenIdChange?: (openId: SourceSheet) => void;
  contactLinks: ContactLink[];
  poRecipientEmail: string | null | undefined;
  defaultFreightMarkup: string | null | undefined;
}) {
  const [ownOpenId, setOwnOpenId] = useState<SourceSheet>(null);
  const openId = controlledOpenId !== undefined ? controlledOpenId : ownOpenId;
  const setOpenId = onOpenIdChange ?? setOwnOpenId;
  const reorder = useReorderSourcingOptions(manufacturerUuid);
  const createDirect = useCreateSourcingOption(manufacturerUuid);
  const ordered = [...options].sort((a, b) => a.rank - b.rank);
  const peopleFor = (optionId: number) =>
    contactLinks
      .filter((link) => link.sourcing_option_id === optionId)
      .sort(
        (a, b) => Number(isCc(a.role)) - Number(isCc(b.role)) || a.id - b.id,
      );
  const open =
    typeof openId === "number"
      ? ordered.find((option) => option.id === openId)
      : undefined;
  const untied = contactLinks
    .filter((link) => link.sourcing_option_id == null)
    .sort((a, b) => contactOrder(a) - contactOrder(b) || a.id - b.id);

  const move = (index: number, to: number) => {
    const ids = ordered.map((option) => option.id);
    const [moved] = ids.splice(index, 1);
    if (moved === undefined) return;
    ids.splice(to, 0, moved);
    const option = ordered[index];
    reorder.mutate(ids, {
      onSuccess: () => {
        if (to === 0 && option)
          toast.success(`${sourceName(option)} is now preferred`);
      },
      onError: (error) =>
        toast.error(errorDetail(error, "Could not reorder sources")),
    });
  };

  return (
    <section
      aria-labelledby="mfr-sourcing"
      className="overflow-hidden rounded-xl border bg-card"
    >
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
        <h2 id="mfr-sourcing" className="text-sm font-semibold">
          Sourcing
        </h2>
        <Button size="sm" variant="outline" onClick={() => setOpenId("new")}>
          <Plus className="size-3.5" />
          Add source
        </Button>
      </div>

      {ordered.length === 0 ? (
        <button
          type="button"
          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50 disabled:opacity-60"
          aria-label="Open Direct"
          disabled={createDirect.isPending}
          onClick={() =>
            // The first source takes over the manufacturer's current terms, so opening it changes nothing.
            createDirect.mutate(
              {},
              {
                onSuccess: (created) => setOpenId(created.id),
                onError: (error) =>
                  toast.error(
                    errorDetail(
                      error,
                      "Could not open the manufacturer's terms",
                    ),
                  ),
              },
            )
          }
        >
          <span
            className="flex size-6 shrink-0 items-center justify-center rounded-full border border-foreground bg-foreground font-mono text-xs text-background"
            aria-hidden="true"
          >
            1
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-baseline gap-x-2 text-sm font-semibold">
              Direct
              <span className="text-xs font-medium text-muted-foreground">
                Preferred
              </span>
            </span>
            <span className="block text-xs text-muted-foreground">
              Manufacturer
            </span>
          </span>
          <ChevronRight
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </button>
      ) : (
        <ol className="divide-y">
          {ordered.map((option, index) => {
            const preferred = index === 0;
            const people = peopleFor(option.id);
            const to = people.find(
              (link) => isOrderRole(link.role) && !isCc(link.role),
            );
            const details = [
              option.supplier
                ? SUPPLIER_KIND_LABELS[option.supplier.kind]
                : "Manufacturer",
              option.buying_group ? `via ${option.buying_group.name}` : null,
              option.product_scope ? `${option.product_scope} only` : null,
            ].filter(Boolean);
            const terms = [
              orderMethodLabel(option),
              describePaymentTerms(option),
              leadTime(option.lead_time_days),
            ].filter((item) => item && item !== "Not set");
            const recipientElsewhere =
              preferred &&
              option.order_method === "email" &&
              poRecipientEmail != null &&
              !people.some((link) => link.contact.email === poRecipientEmail);
            return (
              <li
                key={option.id}
                className={cn("flex items-stretch", preferred && "bg-muted/30")}
              >
                <button
                  type="button"
                  className="grid min-w-0 flex-1 gap-x-6 gap-y-1 px-4 py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]"
                  aria-label={`Open ${sourceName(option)}`}
                  onClick={() => setOpenId(option.id)}
                >
                  <span className="flex min-w-0 gap-3">
                    <span
                      className={cn(
                        "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border font-mono text-xs tabular-nums",
                        preferred &&
                          "border-foreground bg-foreground text-background",
                      )}
                      aria-hidden="true"
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-baseline gap-x-2 text-sm font-semibold">
                        <span className="break-words">
                          {sourceName(option)}
                        </span>
                        <span className="text-xs font-medium text-muted-foreground">
                          {preferred ? "Preferred" : "Backup"}
                        </span>
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {details.join(" · ")}
                      </span>
                    </span>
                  </span>
                  <span className="min-w-0 pl-9 text-xs text-muted-foreground lg:pl-0 lg:pt-0.5">
                    {terms.join(" · ")}
                  </span>
                  <span className="min-w-0 pl-9 text-sm lg:pl-0">
                    {to ? (
                      <span className="flex min-w-0 items-center gap-1.5">
                        {preferred &&
                          option.order_method === "email" &&
                          to.contact.email === poRecipientEmail && (
                            <Mail
                              className="size-3.5 shrink-0"
                              aria-label="Receives POs"
                            />
                          )}
                        <span className="truncate">{to.contact.email}</span>
                        {people.length > 1 && (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            +{people.length - 1}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        No order contact
                      </span>
                    )}
                    {recipientElsewhere && (
                      <span className="mt-0.5 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                        <TriangleAlert
                          className="mt-px size-3.5 shrink-0"
                          aria-hidden="true"
                        />
                        <span className="min-w-0 break-words">
                          POs still go to {poRecipientEmail}
                        </span>
                      </span>
                    )}
                  </span>
                </button>
                <div className="flex shrink-0 items-center justify-end gap-1 pr-2 lg:w-56">
                  {!preferred && (
                    <LoadingButton
                      size="sm"
                      variant="outline"
                      className="max-sm:hidden"
                      loading={
                        reorder.isPending &&
                        reorder.variables?.[0] === option.id
                      }
                      disabled={reorder.isPending}
                      onClick={() => move(index, 0)}
                    >
                      Make preferred
                    </LoadingButton>
                  )}
                  <div className="flex flex-col sm:flex-row">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      aria-label={`Move ${sourceName(option)} up`}
                      disabled={index === 0 || reorder.isPending}
                      onClick={() => move(index, index - 1)}
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      aria-label={`Move ${sourceName(option)} down`}
                      disabled={
                        index === ordered.length - 1 || reorder.isPending
                      }
                      onClick={() => move(index, index + 1)}
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                  </div>
                  <ChevronRight
                    className="size-4 text-muted-foreground max-sm:hidden"
                    aria-hidden="true"
                  />
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {untied.length > 0 && (
        <section
          aria-labelledby="mfr-untied-contacts"
          className="border-t px-4 py-3"
        >
          <h3
            id="mfr-untied-contacts"
            className="mb-2 text-xs font-medium text-muted-foreground"
          >
            Not on a source
          </h3>
          <ul className="divide-y rounded-lg border">
            {untied.map((link) => (
              <ContactRow
                key={link.id}
                manufacturerUuid={manufacturerUuid}
                link={link}
                receivesPos={null}
                sources={ordered}
              />
            ))}
          </ul>
        </section>
      )}

      <Sheet
        open={openId !== null}
        onOpenChange={(next) => {
          if (!next) setOpenId(null);
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
        >
          {openId === "new" ? (
            <NewSourceForm
              manufacturerUuid={manufacturerUuid}
              manufacturerName={manufacturerName}
              onCreated={(created) => setOpenId(created.id)}
              onCancel={() => setOpenId(null)}
            />
          ) : open ? (
            <SourceDetails
              key={open.id}
              manufacturerUuid={manufacturerUuid}
              manufacturerName={manufacturerName}
              option={open}
              preferred={open.rank === 1}
              onlySource={ordered.length === 1}
              people={peopleFor(open.id)}
              poRecipientEmail={poRecipientEmail}
              defaultFreightMarkup={defaultFreightMarkup}
              onMakePreferred={() => {
                const index = ordered.findIndex(
                  (option) => option.id === open.id,
                );
                if (index > 0) move(index, 0);
              }}
              makingPreferred={reorder.isPending}
              onRemoved={() => setOpenId(null)}
              discounts={discounts}
              freightRules={freightRules}
              feeRules={feeRules}
              caseTerms={caseTerms ?? DEFAULT_CASE_TERMS}
              paymentMethod={paymentMethod}
              shipsToResidential={shipsToResidential ?? true}
              specialPrograms={specialPrograms}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </section>
  );
}

function NewSourceForm({
  manufacturerUuid,
  manufacturerName,
  onCreated,
  onCancel,
}: {
  manufacturerUuid: string;
  manufacturerName: string;
  onCreated: (option: SourcingOption) => void;
  onCancel: () => void;
}) {
  const create = useCreateSourcingOption(manufacturerUuid);
  const [supplier, setSupplier] = useState<Supplier | null>(null);

  return (
    <>
      <SheetHeader className="border-b px-4 py-4 sm:px-6">
        <SheetTitle>Add source</SheetTitle>
        <SheetDescription>{manufacturerName}</SheetDescription>
      </SheetHeader>
      <div className="flex-1 space-y-1.5 overflow-y-auto px-4 py-5 sm:px-6">
        <Label htmlFor="new-source-supplier">Order through</Label>
        <SupplierPicker
          id="new-source-supplier"
          value={supplier?.id ?? null}
          onChange={setSupplier}
          disabled={create.isPending}
        />
      </div>
      <SheetFooter className="gap-2 border-t px-4 py-3 sm:px-6">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={create.isPending}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={create.isPending}
          loadingText="Adding..."
          onClick={() =>
            create.mutate(
              { supplier_id: supplier?.id ?? null },
              {
                onSuccess: (created) => {
                  toast.success(`${created.supplier?.name ?? "Direct"} added`);
                  onCreated(created);
                },
                onError: (error) =>
                  toast.error(errorDetail(error, "Could not add source")),
              },
            )
          }
        >
          Add source
        </LoadingButton>
      </SheetFooter>
    </>
  );
}

function SourceDetails({
  manufacturerUuid,
  manufacturerName,
  option,
  preferred,
  onlySource,
  people,
  poRecipientEmail,
  defaultFreightMarkup,
  onMakePreferred,
  makingPreferred,
  onRemoved,
  discounts,
  freightRules,
  feeRules,
  caseTerms,
  paymentMethod,
  shipsToResidential,
  specialPrograms,
}: {
  manufacturerUuid: string;
  manufacturerName: string;
  option: SourcingOption;
  preferred: boolean;
  onlySource: boolean;
  people: ContactLink[];
  poRecipientEmail: string | null | undefined;
  defaultFreightMarkup: string | null | undefined;
  onMakePreferred: () => void;
  makingPreferred: boolean;
  onRemoved: () => void;
  discounts: Discount[];
  freightRules: FreightRuleData[];
  feeRules: FeeRule[];
  caseTerms: ManufacturerCaseTerms;
  paymentMethod?: ManufacturerPaymentMethod | null;
  shipsToResidential: boolean;
  specialPrograms: SpecialProgramSummary[];
}) {
  const saver = useSaveSourcingOption(manufacturerUuid, option.id);
  const savePaymentMethod = useUpdateManufacturerPaymentMethod(manufacturerUuid);
  const cardFee = useManufacturerCardFees(manufacturerUuid).data?.find(
    (fee) => fee.sourcing_option_id === option.id,
  );
  const remove = useDeleteSourcingOption(manufacturerUuid);
  const suppliers = useSuppliers();
  const buyingGroups = (suppliers.data ?? []).filter(
    (supplier) => supplier.kind === "buying_group",
  );
  const kind = option.supplier
    ? SUPPLIER_KIND_LABELS[option.supplier.kind]
    : "Manufacturer";

  return (
    <>
      <SheetHeader className="border-b px-4 py-4 sm:px-6">
        <SheetTitle className="flex flex-wrap items-baseline gap-x-2 pr-8">
          {sourceName(option)}
          <span className="text-sm font-medium text-muted-foreground">
            {preferred ? "Preferred" : `Backup ${option.rank - 1}`}
          </span>
        </SheetTitle>
        <SheetDescription>
          {kind} · {manufacturerName}
        </SheetDescription>
      </SheetHeader>

      <div className="flex-1 space-y-6 overflow-y-auto px-4 py-5 sm:px-6">
        <div className="overflow-hidden rounded-lg border">
          <div className="-mb-px -mr-px grid grid-cols-1 sm:grid-cols-2">
            <SettingCell
              label="Order through"
              htmlFor="source-supplier"
              className="sm:col-span-2"
            >
              <SupplierPicker
                id="source-supplier"
                value={option.supplier_id ?? null}
                onChange={(supplier) =>
                  saver.save({ supplier_id: supplier?.id ?? null }, "source")
                }
                disabled={saver.isSaving("supplier_id")}
              />
            </SettingCell>
            {buyingGroups.length > 0 && (
              <SettingCell label="Buying group">
                <ReviewChoiceGroup
                  label="Buying group"
                  value={
                    option.buying_group_id != null
                      ? String(option.buying_group_id)
                      : "none"
                  }
                  options={[
                    { value: "none", label: "None" },
                    ...buyingGroups.map((group) => ({
                      value: String(group.id),
                      label: group.name,
                    })),
                  ]}
                  onChange={(value) =>
                    saver.save(
                      {
                        buying_group_id:
                          value === "none" ? null : Number(value),
                      },
                      "buying group",
                    )
                  }
                  disabled={saver.isSaving("buying_group_id")}
                />
              </SettingCell>
            )}
            <SettingCell label="Covers" className="sm:col-span-2">
              {preferred ? (
                <p className="py-1 text-sm">
                  Everything no backup source covers
                </p>
              ) : (
                <SourceCategoriesField
                  manufacturerUuid={manufacturerUuid}
                  optionId={option.id}
                />
              )}
              {option.product_scope && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Noted as &ldquo;{option.product_scope}&rdquo;
                </p>
              )}
            </SettingCell>
            <SettingCell label="Orders" className="sm:col-span-2">
              <ReviewChoiceGroup
                label="How orders are placed"
                value={option.order_method}
                options={ORDER_METHOD_OPTIONS}
                onChange={(value) =>
                  saver.save({ order_method: value }, "ordering")
                }
                disabled={saver.isSaving("order_method")}
              />
            </SettingCell>
            {option.order_method !== "email" && (
              <SettingCell label="Order website" className="sm:col-span-2">
                <POInlineEditableField
                  value={option.order_url ?? ""}
                  placeholder="Not set"
                  ariaLabel="Edit order website"
                  pending={saver.isSaving("order_url")}
                  showEditIcon
                  buttonClassName={cn(
                    "-ml-2 font-medium",
                    !option.order_url && "font-normal text-muted-foreground",
                  )}
                  onConfirm={(value) =>
                    saver.save(
                      { order_url: value.trim() || null },
                      "order website",
                    )
                  }
                />
                {option.order_url && (
                  // eslint-disable-next-line no-restricted-syntax -- external ordering site
                  <a
                    href={websiteHref(option.order_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label="Open order website"
                  >
                    <ExternalLink className="size-3.5" />
                  </a>
                )}
              </SettingCell>
            )}
            <SettingCell label="Account" htmlFor="source-account">
              <Select
                value={option.account_status ?? "unknown"}
                onValueChange={(value) =>
                  saver.save(
                    {
                      account_status:
                        value === "unknown" ? null : (value as AccountStatus),
                    },
                    "account",
                  )
                }
                disabled={saver.isSaving("account_status")}
              >
                <SelectTrigger id="source-account" className="h-8 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">Not recorded</SelectItem>
                  {(Object.keys(ACCOUNT_STATUS_LABELS) as AccountStatus[]).map(
                    (status) => (
                      <SelectItem key={status} value={status}>
                        {ACCOUNT_STATUS_LABELS[status]}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </SettingCell>
            <SettingCell label="Account #">
              <POInlineEditableField
                value={option.account_number ?? ""}
                placeholder="Not set"
                ariaLabel="Edit account number"
                pending={saver.isSaving("account_number")}
                showEditIcon
                buttonClassName={cn(
                  "-ml-2 font-medium",
                  !option.account_number && "font-normal text-muted-foreground",
                )}
                onConfirm={(value) =>
                  saver.save(
                    { account_number: value.trim() || null },
                    "account number",
                  )
                }
              />
            </SettingCell>
          </div>
        </div>

        {option.supplier && (
          <SupplierAddress
            manufacturerUuid={manufacturerUuid}
            supplier={option.supplier}
          />
        )}

        <SourceContacts
          manufacturerUuid={manufacturerUuid}
          option={option}
          people={people}
          receivesPos={
            preferred && option.order_method === "email"
              ? poRecipientEmail
              : null
          }
        />

        {preferred ? (
          <LiveDiscounts
            manufacturerUuid={manufacturerUuid}
            discounts={discounts}
          />
        ) : (
          <BackupDiscounts
            manufacturerUuid={manufacturerUuid}
            option={option}
            categories={discounts}
          />
        )}

        <section aria-labelledby="source-terms">
          <h3 id="source-terms" className="mb-2 text-sm font-semibold">
            Terms
          </h3>
          <div className="overflow-hidden rounded-lg border">
            <PurchasingTermsGrid
              manufacturerUuid={manufacturerUuid}
              terms={option}
              defaultFreightMarkup={defaultFreightMarkup}
              save={(body, label) => saver.saveTerms(body, label)}
              isSaving={(field) => saver.isSaving(field)}
              mutation={saver.termsMutation}
              feeRules={preferred ? feeRules : undefined}
              caseTerms={preferred ? caseTerms : undefined}
              shipsToResidential={preferred ? shipsToResidential : undefined}
              paymentMethod={
                preferred
                  ? {
                      value: savePaymentMethod.isPending
                        ? savePaymentMethod.variables
                        : paymentMethod,
                      onChange: (value) =>
                        savePaymentMethod.mutate(value, {
                          onError: (error) =>
                            toast.error(
                              errorDetail(error, "Could not save how we pay"),
                            ),
                        }),
                      pending: savePaymentMethod.isPending,
                    }
                  : undefined
              }
              cardFee={
                cardFee ? (
                  <CardFeeField
                    manufacturerUuid={manufacturerUuid}
                    fee={cardFee}
                  />
                ) : undefined
              }
              leadTimeCheck={
                preferred ? (
                  <LeadTimeCheckSetting manufacturerUuid={manufacturerUuid} />
                ) : undefined
              }
            />
          </div>
        </section>

        <FreightProgram
          manufacturerUuid={manufacturerUuid}
          optionId={preferred ? undefined : option.id}
          rules={preferred ? freightRules : (option.freight_rules ?? [])}
        />

        <SourceSpecials
          manufacturerUuid={manufacturerUuid}
          option={option}
          preferred={preferred}
          programs={specialPrograms}
        />
      </div>

      <SheetFooter className="gap-2 border-t px-4 py-3 sm:px-6">
        {!onlySource && (
          <ActionPopover
            trigger={
              <LoadingButton
                variant="ghost"
                className="text-destructive sm:mr-auto"
                loading={remove.isPending}
                loadingText="Removing..."
              >
                <Trash2 className="size-4" />
                Remove source
              </LoadingButton>
            }
            title={
              people.length
                ? `Remove ${sourceName(option)} and its ${people.length} order ${people.length === 1 ? "contact" : "contacts"}?`
                : `Remove ${sourceName(option)}?`
            }
            confirmLabel="Remove source"
            confirmVariant="destructive"
            onConfirm={(_note, close) => {
              close();
              onRemoved();
              remove.mutate(option.id, {
                onError: (error) =>
                  toast.error(errorDetail(error, "Could not remove source")),
              });
            }}
          />
        )}
        {!preferred && (
          <LoadingButton
            loading={makingPreferred}
            loadingText="Saving..."
            onClick={onMakePreferred}
          >
            Make preferred
          </LoadingButton>
        )}
      </SheetFooter>
    </>
  );
}

const SUPPLIER_ADDRESS_FIELDS: Array<{
  field: "address1" | "address2" | "city" | "state" | "zip_code" | "phone";
  label: string;
}> = [
  { field: "address1", label: "Street" },
  { field: "address2", label: "Suite" },
  { field: "city", label: "City" },
  { field: "state", label: "State" },
  { field: "zip_code", label: "ZIP" },
  { field: "phone", label: "Phone" },
];

/** The supplier's address and phone, printed under the manufacturer on POs bought through it. */
function SupplierAddress({
  manufacturerUuid,
  supplier,
}: {
  manufacturerUuid: string;
  supplier: Supplier;
}) {
  const update = useUpdateSupplier(manufacturerUuid);
  const pendingField = update.isPending
    ? Object.keys(update.variables ?? {}).find((key) => key !== "id")
    : undefined;
  return (
    <section aria-labelledby="supplier-address">
      <h3 id="supplier-address" className="mb-2 text-sm font-semibold">
        {supplier.name} address
      </h3>
      <div className="overflow-hidden rounded-lg border">
        <div className="-mb-px -mr-px grid grid-cols-1 sm:grid-cols-2">
          {SUPPLIER_ADDRESS_FIELDS.map(({ field, label }) => {
            const value = supplier.address?.[field] ?? "";
            return (
              <SettingCell key={field} label={label}>
                <POInlineEditableField
                  value={value}
                  displayValue={
                    field === "phone" && value ? formatPhone(value) : undefined
                  }
                  placeholder="Not set"
                  ariaLabel={`Edit ${supplier.name} ${label.toLowerCase()}`}
                  pending={pendingField === field}
                  showEditIcon
                  buttonClassName={cn(
                    "-ml-2 font-medium",
                    !value && "font-normal text-muted-foreground",
                  )}
                  onConfirm={(next) =>
                    update.mutate(
                      { id: supplier.id, [field]: next.trim() || null },
                      {
                        onError: (error) =>
                          toast.error(
                            errorDetail(
                              error,
                              `Could not save ${supplier.name}'s ${label.toLowerCase()}`,
                            ),
                          ),
                      },
                    )
                  }
                />
              </SettingCell>
            );
          })}
        </div>
      </div>
    </section>
  );
}

type ContactType =
  | "order"
  | "order_cc"
  | "spec"
  | "spec_cc"
  | "accounting"
  | "customer_service"
  | "service"
  | "data"
  | "data_cc";

const CONTACT_TYPES: Array<{
  value: ContactType;
  label: string;
  role: string;
}> = [
  { value: "order", label: "Orders", role: "po_primary" },
  { value: "order_cc", label: "Orders CC", role: "po_primary_cc" },
  { value: "spec", label: "Spec credit", role: "spec_credit" },
  { value: "spec_cc", label: "Spec credit CC", role: "spec_credit_cc" },
  // Invoices and payments, customer service, warranty claims (USA-1473). They never receive POs.
  { value: "accounting", label: "Accounting", role: "accounting" },
  { value: "customer_service", label: "Customer service", role: "customer_service" },
  { value: "service", label: "Service", role: "service" },
  // Who gets the data@ emails, such as the weekly lead-time check (USA-1474).
  { value: "data", label: "Data", role: "data" },
  { value: "data_cc", label: "Data CC", role: "data_cc" },
];

function contactType(role: string): ContactType {
  if (role === "accounting" || role === "customer_service" || role === "service")
    return role;
  const spec = role.startsWith("spec_credit");
  if (spec) return isCc(role) ? "spec_cc" : "spec";
  if (role === "data" || role === "data_cc") return role;
  return isCc(role) ? "order_cc" : "order";
}

function contactOrder(link: ContactLink): number {
  return CONTACT_TYPES.findIndex(
    (type) => type.value === contactType(link.role),
  );
}

/** Everyone who orders through a source or handles its spec credit requests. */
function SourceContacts({
  manufacturerUuid,
  option,
  people,
  receivesPos,
}: {
  manufacturerUuid: string;
  option: SourcingOption;
  people: ContactLink[];
  receivesPos: string | null | undefined;
}) {
  const add = useAddSourcingContact(manufacturerUuid);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [type, setType] = useState<ContactType>(
    people.some((link) => contactType(link.role) === "order")
      ? "order_cc"
      : "order",
  );
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const sorted = [...people].sort(
    (a, b) => contactOrder(a) - contactOrder(b) || a.id - b.id,
  );

  const submit = () => {
    if (!emailValid || add.isPending) return;
    add.mutate(
      {
        email: email.trim(),
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        role:
          CONTACT_TYPES.find((item) => item.value === type)?.role ??
          "po_primary",
        sourcing_option_id: option.id,
      },
      {
        onSuccess: () => {
          setEmail("");
          setName("");
          setPhone("");
        },
        onError: (error) =>
          toast.error(errorDetail(error, "Could not add contact")),
      },
    );
  };

  return (
    <fieldset>
      <legend className="mb-2 text-sm font-semibold">Contacts</legend>
      {sorted.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {sorted.map((link) => (
            <ContactRow
              key={link.id}
              manufacturerUuid={manufacturerUuid}
              link={link}
              receivesPos={receivesPos}
            />
          ))}
        </ul>
      )}
      <div className="mt-2 space-y-2">
        <ReviewChoiceGroup
          label="Contact type"
          value={type}
          options={CONTACT_TYPES.map(({ value, label }) => ({ value, label }))}
          onChange={setType}
          disabled={add.isPending}
        />
        <div className="flex flex-wrap items-center gap-2">
          <InlineFieldInput
            aria-label="New contact email"
            type="email"
            inputMode="email"
            value={email}
            placeholder="name@example.com"
            className="min-w-48 flex-1"
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit();
            }}
            disabled={add.isPending}
          />
          <InlineFieldInput
            aria-label="New contact name"
            value={name}
            placeholder="Name (optional)"
            className="min-w-36 flex-1"
            onChange={(event) => setName(event.target.value)}
            disabled={add.isPending}
          />
          <InlineFieldInput
            aria-label="New contact phone"
            type="tel"
            inputMode="tel"
            value={phone}
            placeholder="Phone (optional)"
            className="min-w-36 flex-1"
            onChange={(event) => setPhone(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit();
            }}
            disabled={add.isPending}
          />
          <LoadingButton
            variant="outline"
            loading={add.isPending}
            loadingText="Adding..."
            onClick={submit}
            disabled={!emailValid}
          >
            <Plus className="size-3.5" />
            Add
          </LoadingButton>
        </div>
      </div>
    </fieldset>
  );
}

/** The person's phone, so Quo calls from it are filed under this manufacturer (USA-217). */
function ContactPhone({ contactId, phone, label }: { contactId: number; phone: string | null; label: string }) {
  const update = useUpdateContact(contactId);
  return (
    <span className="flex shrink-0 items-center gap-1 text-muted-foreground max-sm:order-last max-sm:basis-full">
      <Phone className="size-3.5 shrink-0" aria-hidden />
      <POInlineEditableField
      value={phone ?? ""}
      displayValue={phone ? formatPhone(phone) : undefined}
      placeholder="Add phone"
      ariaLabel={`Edit phone for ${label}`}
      pending={update.isPending}
      buttonClassName="w-32 tabular-nums"
      onConfirm={(value) => {
        const next = value.trim();
        if (next === (phone ?? "")) return;
        update.mutate(
          { phone: next || null },
          { onError: (error) => toast.error(errorDetail(error, "Could not save phone")) },
        );
      }}
    />
    </span>
  );
}

function ContactRow({
  manufacturerUuid,
  link,
  receivesPos,
  sources,
}: {
  manufacturerUuid: string;
  link: ContactLink;
  receivesPos: string | null | undefined;
  /** Offer a move to one of these sources (for people not on a source yet). */
  sources?: SourcingOption[];
}) {
  const update = useUpdateManufacturerContact(manufacturerUuid);
  const remove = useRemoveManufacturerContact(manufacturerUuid);
  const pending =
    update.isPending || (remove.isPending && remove.variables === link.id);
  const onError = (error: unknown) =>
    toast.error(errorDetail(error, "Could not save contact"));
  return (
    <li className="flex min-w-0 flex-wrap items-center gap-2 px-3 py-1.5 text-sm sm:flex-nowrap">
      <Select
        value={contactType(link.role)}
        onValueChange={(value) =>
          update.mutate(
            {
              linkId: link.id,
              role: CONTACT_TYPES.find((item) => item.value === value)?.role,
            },
            { onError },
          )
        }
        disabled={pending}
      >
        <SelectTrigger
          aria-label={`Type for ${link.contact.email}`}
          className="h-8 w-36 shrink-0"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONTACT_TYPES.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="order-last flex min-w-0 basis-full items-center gap-1.5 sm:order-none sm:basis-auto sm:flex-1">
        {receivesPos === link.contact.email && (
          <Mail className="size-3.5 shrink-0" aria-label="Receives POs" />
        )}
        <POInlineEditableField
          value={link.contact.email}
          displayValue={
            link.contact.name
              ? `${link.contact.name} · ${link.contact.email}`
              : undefined
          }
          ariaLabel={`Edit email for ${link.contact.email}`}
          pending={
            update.isPending &&
            update.variables?.linkId === link.id &&
            "email" in (update.variables ?? {})
          }
          buttonClassName="-ml-2 min-w-0 truncate"
          onConfirm={(value) => {
            const next = value.trim();
            if (next && next !== link.contact.email)
              update.mutate({ linkId: link.id, email: next }, { onError });
          }}
        />
      </span>
      <ContactPhone contactId={link.contact.id} phone={link.contact.phone ?? null} label={link.contact.email} />
      <Link
        to="/contacts/$contactId"
        params={{ contactId: String(link.contact.id) }}
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground max-sm:ml-auto"
        aria-label={`Open contact ${link.contact.email}`}
      >
        <UserRound className="size-3.5" />
      </Link>
      {sources && sources.length > 0 && (
        <Select
          value=""
          onValueChange={(value) =>
            update.mutate(
              { linkId: link.id, sourcing_option_id: Number(value) },
              { onError },
            )
          }
          disabled={pending}
        >
          <SelectTrigger
            aria-label={`Move ${link.contact.email} to a source`}
            className="h-8 w-40 shrink-0"
          >
            <SelectValue placeholder="Move to source" />
          </SelectTrigger>
          <SelectContent>
            {sources.map((option) => (
              <SelectItem key={option.id} value={String(option.id)}>
                {sourceName(option)}
                {option.buying_group ? ` via ${option.buying_group.name}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <LoadingButton
        variant="ghost"
        size="icon"
        className="size-8 shrink-0 text-destructive"
        aria-label={`Remove ${link.contact.email}`}
        loading={remove.isPending && remove.variables === link.id}
        onClick={() =>
          remove.mutate(link.id, {
            onError: () => toast.error("Could not remove contact"),
          })
        }
      >
        <Trash2 className="size-4" />
      </LoadingButton>
    </li>
  );
}
